import { PublicPlan } from '@wix/ambassador-pricing-plans-v2-plan/types';
import { Plan } from '@wix/ambassador-pricing-plans-v3-plan/types';
import { FormattedAdditionalFee } from '@wix/pricing-plans-tpa-api';
import {
  allowsFutureStartDate,
  getSetupFeeAmount,
  getSetupFeeName,
  getVariantSetupFeeAmount,
  getVariantSetupFeeName,
  hasSetupFee,
  hasSetupFeeVariant,
  isFreePlan,
  isFreeVariant,
  isOneTimePricingVariant,
  isPlanV3,
  isRecurringVariant,
  isUnlimitedVariant,
} from '@wix/pricing-plans-utils';
import { getThankYouPageSettings, ThankYouPageSettings } from '@wix/pricing-plans-utils/client-data';
import { CreateCurrencyFormatter, TFunction } from '@wix/yoshi-flow-editor';
import { NO_BREAK_SPACE } from '../constants';
import { isOneTimePlan } from './domainUtils';
import { getFormattedAdditionalFees as getFormattedAdditionalFeesV2 } from './get-formatted-additional-fees';
import { getPeriodLabel as getPeriodLabelV2 } from './getPeriodLabel';
import { getPlanValidityCycle } from './getPlanValidityCycle';
import { getVariantAdditionalFees, getVariantDuration, getVariantPeriodLabel } from './plan-v3-utils';

interface PlanDisplayFlags {
  recurringPlansExist: boolean;
  freeTrialDaysExist: boolean;
  setupFeesExist: boolean;
  validityCyclesExist: boolean;
  anyBenefits: boolean;
}

export function getPlanDisplayFlags(plans: PublicPlan[] | Plan[], t: TFunction): PlanDisplayFlags {
  if (isV3PlanArray(plans)) {
    return {
      recurringPlansExist: plans.some((p) => isRecurringVariant(p.pricingVariants![0])),
      freeTrialDaysExist: plans.some((p) => !!p.pricingVariants?.[0]?.freeTrialDays),
      setupFeesExist: plans.some((p) => !!p.pricingVariants?.[0]?.fees?.length),
      validityCyclesExist: plans.some((p) => getVariantDuration(p.pricingVariants![0], t) !== NO_BREAK_SPACE),
      anyBenefits: plans.some((p) => !!p.perks?.length),
    };
  }
  return {
    recurringPlansExist: plans.some((p) => !!p.pricing?.subscription),
    freeTrialDaysExist: plans.some((p) => !!p.pricing?.freeTrialDays),
    setupFeesExist: plans.some((p) => !!p.pricing?.feeConfigs?.length),
    validityCyclesExist: plans.some((p) => getPlanValidityCycle(p, t) !== NO_BREAK_SPACE),
    anyBenefits: plans.some((p) => !!p.perks?.values?.length),
  };
}

export function getIsPlanRecurring(plan: PublicPlan | Plan): boolean {
  return isPlanV3(plan) ? isRecurringVariant(plan.pricingVariants![0]) : !!plan.pricing?.subscription;
}

export function getPlanHasFees(plan: PublicPlan | Plan): boolean {
  return isPlanV3(plan)
    ? (plan.pricingVariants?.[0].fees?.length ?? 0) > 0
    : (plan.pricing?.feeConfigs?.length ?? 0) > 0;
}

export function getPlanHasValidity(plan: PublicPlan | Plan, t: TFunction): boolean {
  return isPlanV3(plan)
    ? getVariantDuration(plan.pricingVariants![0], t) !== NO_BREAK_SPACE
    : getPlanValidityCycle(plan, t) !== NO_BREAK_SPACE;
}

export function isV3PlanArray(plans: PublicPlan[] | Plan[]): plans is Plan[] {
  return plans.length > 0 && isPlanV3(plans[0]);
}

export function isV2PlanArray(plans: PublicPlan[] | Plan[]): plans is PublicPlan[] {
  return plans.length > 0 && !isPlanV3(plans[0]);
}

export function getFormattedFees(params: {
  plan: PublicPlan | Plan;
  locale?: string;
  createCurrencyFormatter: CreateCurrencyFormatter;
  hideTrailingFractionZeroes?: boolean;
}): FormattedAdditionalFee[] {
  const { plan, ...rest } = params;

  return isPlanV3(plan)
    ? getVariantAdditionalFees({ variant: plan.pricingVariants![0], currency: plan.currency, ...rest })
    : getFormattedAdditionalFeesV2({ plan, ...rest });
}

export function getRecurrenceText(plan: PublicPlan | Plan, t: TFunction): string {
  if (isPlanV3(plan)) {
    return getVariantPeriodLabel(plan.pricingVariants![0], t) ?? NO_BREAK_SPACE;
  }
  return getPeriodLabelV2(plan.pricing?.subscription, t) ?? NO_BREAK_SPACE;
}

export function getSetupFeeAmountInCents(plan: PublicPlan | Plan): number | undefined {
  if (isPlanV3(plan)) {
    return hasSetupFeeVariant(plan.pricingVariants?.[0])
      ? Math.round(Number(getVariantSetupFeeAmount(plan.pricingVariants?.[0])) * 100)
      : undefined;
  }
  return hasSetupFee(plan) ? Math.round(Number(getSetupFeeAmount(plan)) * 100) : undefined;
}

export function getPlanAllowsFutureStartDate(plan: PublicPlan | Plan): boolean | null | undefined {
  return isPlanV3(plan) ? allowsFutureStartDate(plan.pricingVariants![0]) : plan.allowFutureStartDate;
}

export function getIsFreePlan(plan: PublicPlan | Plan): boolean {
  return isPlanV3(plan) ? isFreeVariant(plan.pricingVariants![0]) : isFreePlan(plan);
}

export function getIsOneTimePlan(plan: PublicPlan | Plan): boolean {
  return isPlanV3(plan) ? isOneTimePricingVariant(plan.pricingVariants![0]) : isOneTimePlan(plan);
}

export function getIsPlanUnlimited(plan: PublicPlan | Plan): boolean {
  if (isPlanV3(plan)) {
    return isUnlimitedVariant(plan.pricingVariants![0]);
  }
  if (plan.pricing?.subscription) {
    return plan.pricing.subscription.cycleCount === 0;
  }
  return !!plan.pricing?.singlePaymentUnlimited;
}

export function getPlanHasSetupFee(plan: PublicPlan | Plan): boolean {
  return isPlanV3(plan) ? Boolean(hasSetupFeeVariant(plan.pricingVariants?.[0])) : Boolean(hasSetupFee(plan));
}

export function getUnifiedPlanPrice(plan: PublicPlan | Plan): { value?: string; currency?: string } {
  if (isPlanV3(plan)) {
    return {
      value: plan.pricingVariants?.[0].pricingStrategies?.[0]?.flatRate?.amount ?? undefined,
      currency: plan.currency ?? undefined,
    };
  }
  return {
    value: plan.pricing?.price?.value,
    currency: plan.pricing?.price?.currency,
  };
}

export function getPlanFreeTrialDays(plan: PublicPlan | Plan): number | undefined {
  return isPlanV3(plan)
    ? plan.pricingVariants?.[0].freeTrialDays ?? undefined
    : plan.pricing?.freeTrialDays ?? undefined;
}

export function getPlanSetupFeeAmount(plan: PublicPlan | Plan): string | undefined {
  return isPlanV3(plan) ? getVariantSetupFeeAmount(plan.pricingVariants?.[0]) : getSetupFeeAmount(plan);
}

export function getPlanSetupFeeName(plan: PublicPlan | Plan): string | undefined {
  return isPlanV3(plan) ? getVariantSetupFeeName(plan.pricingVariants?.[0]) : getSetupFeeName(plan);
}

export function getPlanThankYouPageSettings(plan: PublicPlan | Plan): ThankYouPageSettings | undefined {
  if (isPlanV3(plan)) {
    const s = plan.thankYouPageSettings;
    if (!s) {
      return undefined;
    }
    return { title: s.title, message: s.message, buttonText: s.buttonText, buttonLink: s.buttonLink ?? undefined };
  }
  return getThankYouPageSettings(plan.clientData);
}
